/* eslint-disable @stylistic/jsx-indent */
/* eslint-disable no-param-reassign */
/* eslint-disable @stylistic/max-len */
/* eslint-disable @stylistic/indent */
import React, { useCallback, useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Row, Col, Button } from "react-bootstrap";

// Store hooks/selectors and actions
import store, { UsePortalSelector } from "../../../../Store";
import { InitialAssemblyState, InitialShadeState, updateAssemblies } from "../../../../Store/entities/assemblies";
import { useAssembliesByQuoteId } from "../../../../Store/entities/assemblies/hooks";
import { useAssemblyErrorsByStore } from "../../../../Store/entities/assemblies/errors/hooks";
import { useAssemblyOptions, useGetColorOptions, useGetFullPaletteList } from "../../../../Store/entities/assemblyOptions/hooks";
import { useIndoorFabrics } from "../../../../Store/entities/hooks";
import { useQuoteById, useQuoteIdAndDispatch, useQuoteTypeNameByQuoteId } from "../../../../Store/entities/quotes/hooks";
import { useQuoteCombination } from "../../../../Store/entities/combinations/hooks";
import { useUser } from "../../../../Store/user/hooks";

// Components and UI helpers
import PriceErrors from "../../../../Quote/PriceErrors";
import MassEditIndoorInner from "./MassEditIndoorInner";
import { useMassEditIndoorColorOptions } from "./MassEditIndoorColorLogic";

// Option builders and API
import { useNewIndoorShadeOptions } from "../../../../Store/entities/assemblies/IndoorHooks.ts";
import apiCalls from "../../../../PowerShadesAPIFunctions";

// Types
import { AssemblyViewModelFromAssemblyStore } from "../../../../Store/entities/assemblies/types";
import type { AssemblyStore, IndoorShadeConfiguration, IndoorShadeOptionsCoupledDict, IndoorShadeOptionsDict, ShadeStore } from "../../../../Store/entities/assemblies/types";
import { initialQuote } from "../../../../Store/entities/quotes";
import IndoorShadeOptionsCoupled from "../../../../Store/entities/assemblies/IndoorHooks.ts/IndoorCoupledHooks";

type MassEditIndoorProps = {
    quoteId: number;
    isOpen: boolean;
    onClose: () => void;
};
const MassEditIndoor = ({ quoteId, isOpen, onClose }: MassEditIndoorProps) => {
    // Local UI state and refs
    const [initAssyState, setInitAssyState] = useState({
        ...InitialAssemblyState(),
        unlocked: true,
        shades: [InitialShadeState()],
        quote_id: quoteId
    });
    const [workingAssembly, setWorkingAssembly] = useState(initAssyState);
    const [assembliesLoaded, setAssembliesLoaded] = useState(false);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const [showBands, setShowBands] = useState(false);
    const [editedShadeConfig, setEditedShadeConfig] = useState<IndoorShadeConfiguration | null>(null);

    const popupRef = useRef<HTMLDivElement | null>(null);

    // Store selectors and data sources
    const user = useUser();
    const quote = useQuoteById(quoteId);
    const quoteType = useQuoteTypeNameByQuoteId(quoteId);
    const assemblies = useAssembliesByQuoteId(quoteId);
    const selectedAssemblies = assemblies.filter((assy) => assy.selected);
    const indoorFabrics = useIndoorFabrics(quoteId);
    const assemblyOptions = UsePortalSelector(useAssemblyOptions(quoteId));
    const colorOptions = useGetColorOptions(quoteId);
    const getFullPaletteList = useGetFullPaletteList(quoteId);
    const quoteCombinations = useQuoteCombination(quoteId) ?? { Fascias: [] };

    // Initialize shade options
    const shadeOptionsBase = (useNewIndoorShadeOptions(
        { ...InitialAssemblyState(), ...workingAssembly },
        setWorkingAssembly,
        { fabrics: indoorFabrics, user, quote, assemblyOptions, quoteCombinations }
    ) ?? {}) as IndoorShadeOptionsDict;

    // Grab shared colors/palettes options
    const { shadeOptions, computePaletteColors } = useMassEditIndoorColorOptions({
        selectedAssemblies,
        workingAssembly,
        setWorkingAssembly,
        shadeOptionsBase,
        getFullPaletteList,
        colorOptions,
    });

    // Build shared Motor/Manual options (intersection across all selected assemblies' headers)
    const sharedDriveTypeOptions = React.useMemo(() => {
        // Helper to intersect arrays of option items by their value
        function intersectByValue(lists: any[][]): any[] {
            if (!lists || lists.length === 0) return [];
            const first = lists[0] ?? [];
            const byValue = (arr: any[]) => {
                const m = new Map<string, any>();
                arr.forEach((opt) => {
                    if (opt && typeof opt.value === "string") {
                        m.set(opt.value, opt);
                    }
                });
                return m;
            };
            const firstMap = byValue(first);
            const result: any[] = [];
            firstMap.forEach((opt, val) => {
                const presentInAll = lists.every((lst) => (lst ?? []).some((o) => o?.value === val));
                if (presentInAll) result.push(opt);
            });
            return result.sort((a, b) => (a.value || "").localeCompare(b.value || ""));
        }

        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return { motor: [] as any[], manual: [] as any[] };
        }

        // Collect motor/manual options for each selected assembly's header
    const perAssemblyMotor: any[][] = [];
    const perAssemblyManual: any[][] = [];

        selectedAssemblies.forEach((assy) => {
            const headerName = assy?.header_line;
            const header = headers.find((h: any) => h?.name === headerName);
            const driveTypes = header?.drive_types ?? [];
            const motor = (driveTypes as any[])
                .filter((dt) => dt?.shade_type === "motorized")
                .map((dt) => ({ label: dt?.name ?? "", value: dt?.value ?? "", title: dt?.name ?? "" }))
                .filter((o) => !!o.value);
            const manual = (driveTypes as any[])
                .filter((dt) => dt?.shade_type === "manual")
                .map((dt) => ({ label: dt?.name ?? "", value: dt?.value ?? "", title: dt?.name ?? "" }))
                .filter((o) => !!o.value);
            if (motor.length > 0) perAssemblyMotor.push(motor);
            else perAssemblyMotor.push([]);
            if (manual.length > 0) perAssemblyManual.push(manual);
            else perAssemblyManual.push([]);
        });

        const sharedMotor = intersectByValue(perAssemblyMotor);
        const sharedManual = intersectByValue(perAssemblyManual);

        return { motor: sharedMotor, manual: sharedManual };
    }, [assemblyOptions, selectedAssemblies]);

    // Build shared Hembar Line options across selected assemblies (by header)
    const sharedHembarLineOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        const perAssemblyLines: any[][] = [];

        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const hembars = header?.hembars ?? [];
            const opts = (hembars as any[])
                .map((hb: any) => ({ label: hb?.name ?? "", value: hb?.value ?? "", title: hb?.name ?? "" }))
                .filter((o: any) => !!o.value);
            perAssemblyLines.push(opts);
        });

        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblyLines);
        const labelLookup = new Map<string, any>();
        (perAssemblyLines[0] ?? []).forEach((opt) => labelLookup.set(opt.value, opt));
        const intersected = sharedValues
            .map((val) => labelLookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies]);

    // Build shared Control Side options across selected assemblies (by header)
    const sharedControlSideOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        const perAssemblySides: any[][] = [];
        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const controlSides = header?.control_sides ?? [];
            const opts = (controlSides as any[])
                .map((cs: any) => ({ label: cs?.name ?? "", value: cs?.value ?? "", title: cs?.name ?? "" }))
                .filter((o: any) => !!o.value);
            perAssemblySides.push(opts);
        });

        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblySides);
        const labelLookup = new Map<string, any>();
        (perAssemblySides[0] ?? []).forEach((opt) => labelLookup.set(opt.value, opt));
        const intersected = sharedValues
            .map((val) => labelLookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies]);

    // Build shared Side Channels options across selected assemblies (by header)
    const sharedSideChannelOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        const perAssemblyChannels: any[][] = [];
        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const channels = header?.channels ?? [];
            const opts = (channels as any[])
                .map((sc: any) => ({ label: sc?.name ?? "", value: sc?.value ?? "", title: sc?.name ?? "" }))
                .filter((o: any) => !!o.value);
            perAssemblyChannels.push(opts);
        });

        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblyChannels);
        const labelLookup = new Map<string, any>();
        (perAssemblyChannels[0] ?? []).forEach((opt) => labelLookup.set(opt.value, opt));
        const intersected = sharedValues
            .map((val) => labelLookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies]);

    // Build shared End Caps options across selected assemblies (by header)
    const sharedEndCapsOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        const perAssemblyEndCaps: any[][] = [];
        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const endCaps = header?.end_caps ?? [];
            const opts = (endCaps as any[])
                .map((ec: any) => ({ label: ec?.name ?? "", value: ec?.value ?? "", title: ec?.name ?? "" }))
                .filter((o: any) => !!o.value);
            perAssemblyEndCaps.push(opts);
        });

        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblyEndCaps);
        const labelLookup = new Map<string, any>();
        (perAssemblyEndCaps[0] ?? []).forEach((opt) => labelLookup.set(opt.value, opt));
        const intersected = sharedValues
            .map((val) => labelLookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies]);
    // Build shared Shade Configuration options across selected assemblies
    const sharedShadeConfigOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        // Per assembly: compute allowable shade configurations based on its header and selected drive type (motor/manual specific value)
    const perAssemblyConfigs: any[][] = [];

        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const driveTypes = header?.drive_types ?? [];
            const isMotorized = (assy?.shades?.[0]?.shade_type === "motorized");
            const driveValue = isMotorized ? assy?.shades?.[0]?.motor_type : assy?.shades?.[0]?.manual_chain;
            const driveTypeSelected = (driveTypes as any[]).find((dt) => dt?.value === driveValue);
            const headerConfigs = header?.shade_configurations ?? [];

            // Allowed config values for this drive type. If no drive type is selected, use the intersection across all drive_types for this header.
            let allowedValues: string[] = [];
            if (Array.isArray(driveTypeSelected?.shade_configuration_allowances)) {
                allowedValues = driveTypeSelected.shade_configuration_allowances as string[];
            } else {
                const allAllowLists: string[][] = (driveTypes as any[]).map((dt) => (
                    Array.isArray(dt?.shade_configuration_allowances) ? dt.shade_configuration_allowances : []
                ));
                if (allAllowLists.length > 0) {
                    const uniqueFirst = Array.from(new Set(allAllowLists[0] ?? []));
                    allowedValues = uniqueFirst.filter((val) => allAllowLists.every((lst) => (lst ?? []).includes(val)));
                }
            }

            const options = (allowedValues || [])
                .filter((scVal) => headerConfigs.some((hc: any) => hc?.value === scVal))
                .map((scVal) => {
                    const scMeta = headerConfigs.find((hc: any) => hc?.value === scVal);
                    return {
                        label: scMeta?.name ?? String(scVal),
                        value: String(scVal),
                        title: scMeta?.name ?? String(scVal),
                    };
                });

            perAssemblyConfigs.push(options);
        });

        // Intersect by value across assemblies
        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblyConfigs);
        // Build the intersected options using the first assembly's labels as canonical; fallback to value as label
    const labelLookup = new Map<string, any>();
        (perAssemblyConfigs[0] ?? []).forEach((opt) => labelLookup.set(opt.value, opt as any));
        const intersected = sharedValues
            .map((val) => labelLookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies]);

    // Determine if all selected assemblies share the same Shade Configuration
    const unanimousShadeConfig = React.useMemo(() => {
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0) return "";
        const toConfig = (a: AssemblyStore) => (
            (a?.is_coupled && a?.is_dual) ? "dual_coupled"
                : (a?.is_coupled ? "coupled"
                    : (a?.is_dual ? "dual" : "single"))
        );
        const first = toConfig(selectedAssemblies[0] as any);
        const allSame = selectedAssemblies.every((a) => toConfig(a as any) === first);
        return allSame ? first : "";
    }, [selectedAssemblies]);

    // Build shared Header Mount Type options across selected assemblies
    const sharedMountTypeOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        const perAssemblyMount: any[][] = [];

        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const mtList = header?.mount_types ?? [];
            const isCoupled = !!assy?.is_coupled;
            const allowed = (mtList as any[])
                .filter((mt) => (isCoupled ? mt?.is_coupled_allowed === true : true))
                .map((mt) => ({ label: mt?.name ?? "", value: mt?.value ?? "", title: mt?.name ?? "" }))
                .filter((o) => !!o.value);
            perAssemblyMount.push(allowed);
        });

        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblyMount);
        const lookup = new Map<string, any>();
        (perAssemblyMount[0] ?? []).forEach((opt) => lookup.set(opt.value, opt));
        const intersected = sharedValues
            .map((val) => lookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies]);

    // Build shared Hembar Style options across selected assemblies based on each assembly's current Hembar Line
    const sharedHembarStyleOptions = React.useMemo(() => {
        const headers = (assemblyOptions as any)?.headers ?? [];
        if (!Array.isArray(selectedAssemblies) || selectedAssemblies.length === 0 || !Array.isArray(headers)) {
            return [] as any[];
        }

        const perAssemblyStyles: any[][] = [];

        // If working assembly has a Hembar Line selected, prefer computing styles from that line for all assemblies,
        // so the Style options respond immediately to the user's change.
        const workingHeader = headers.find((h: any) => h?.name === workingAssembly?.header_line);
        const workingHembars = workingHeader?.hembars ?? [];
        const workingLineVal = workingAssembly?.shades?.[0]?.hembar_line;

        selectedAssemblies.forEach((assy) => {
            const header = headers.find((h: any) => h?.name === assy?.header_line);
            const hembars = header?.hembars ?? [];
            const lineVal = workingLineVal && (workingHembars as any[]).some((ho) => ho?.value === workingLineVal)
                ? workingLineVal
                : assy?.shades?.[0]?.hembar_line;
            const currentHembar = (hembars as any[]).find((ho) => ho?.value === lineVal);
            const styles = (currentHembar?.hembar_styles ?? [])
                .map((hs: any) => ({ label: hs?.name ?? "", value: hs?.value ?? "", title: hs?.name ?? "" }))
                .filter((o: any) => !!o.value);
            perAssemblyStyles.push(styles);
        });

        const intersectByValue = (lists: any[][]): string[] => {
            if (!lists || lists.length === 0) return [];
            const first = (lists[0] ?? []).map((o) => o.value);
            return first.filter((val) => lists.every((lst) => (lst ?? []).some((o) => o.value === val)));
        };

        const sharedValues = intersectByValue(perAssemblyStyles);
        const labelLookup = new Map<string, any>();
        (perAssemblyStyles[0] ?? []).forEach((opt) => labelLookup.set(opt.value, opt));
        const intersected = sharedValues
            .map((val) => labelLookup.get(val) ?? { label: val, value: val, title: val })
            .sort((a, b) => a.value.localeCompare(b.value));

        return intersected;
    }, [assemblyOptions, selectedAssemblies, workingAssembly]);

    // Replace MotorType/ManualType options with these shared intersections for the mass edit UI
    const finalShadeOptions = React.useMemo(() => {
        // If no shared options could be computed, fall back to base options
        const motorOpts = (sharedDriveTypeOptions.motor?.length ?? 0) > 0
            ? sharedDriveTypeOptions.motor
            : (shadeOptions?.MotorType?.options ?? []);
        const manualOpts = (sharedDriveTypeOptions.manual?.length ?? 0) > 0
            ? sharedDriveTypeOptions.manual
            : (shadeOptions?.ManualType?.options ?? []);
        const shadeConfigOpts = (sharedShadeConfigOptions?.length ?? 0) > 0
            ? sharedShadeConfigOptions
            : (shadeOptions?.ShadeConfiguration?.options ?? []);
        const mountTypeOpts = (sharedMountTypeOptions?.length ?? 0) > 0
            ? sharedMountTypeOptions
            : (shadeOptions?.HeaderMountType?.options ?? []);
        const hembarStyleOpts = (sharedHembarStyleOptions?.length ?? 0) > 0
            ? sharedHembarStyleOptions
            : (shadeOptions?.HembarStyle?.options ?? []);
        const hembarLineOpts = (sharedHembarLineOptions?.length ?? 0) > 0
            ? sharedHembarLineOptions
            : (shadeOptions?.HembarLine?.options ?? []);
        const controlSideOpts = (sharedControlSideOptions?.length ?? 0) > 0
            ? sharedControlSideOptions
            : (shadeOptions?.ControlSide?.options ?? []);
        const sideChannelOpts = (sharedSideChannelOptions?.length ?? 0) > 0
            ? sharedSideChannelOptions
            : (shadeOptions?.SideChannels?.options ?? []);
        const endCapsOpts = (sharedEndCapsOptions?.length ?? 0) > 0
            ? sharedEndCapsOptions
            : (shadeOptions?.EndCaps?.options ?? []);

        return {
            ...shadeOptions,
            MotorType: {
                ...shadeOptions.MotorType,
                options: motorOpts,
                // Ensure the selected value renders from the list genereated above
                getSelectedOption: () => {
                    const currentVal = shadeOptions.MotorType.getValue();
                    const match = (motorOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            ManualType: {
                ...shadeOptions.ManualType,
                options: manualOpts,
                // Ensure the selected value renders from the list genereated above
                getSelectedOption: () => {
                    const currentVal = shadeOptions.ManualType.getValue();
                    const match = (manualOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            ShadeConfiguration: {
                ...shadeOptions.ShadeConfiguration,
                options: shadeConfigOpts,
                // Prefer unanimous selection for initial display; fall back to underlying working selection
                getSelectedOption: () => {
                    const unanimousVal = (typeof unanimousShadeConfig === "string" && unanimousShadeConfig !== "")
                        ? unanimousShadeConfig
                        : null;
                    const match = unanimousVal
                        ? (shadeConfigOpts as any[]).find((opt) => opt?.value === unanimousVal)
                        : null;
                    return (match as any) || (shadeOptions.ShadeConfiguration.getSelectedOption?.() as any) || { label: "", value: "", title: "" };
                },
                // Do not override getValue — use the base option's getValue so UI reflects the working assembly state
            },
            HeaderMountType: {
                ...shadeOptions.HeaderMountType,
                options: mountTypeOpts,
                getSelectedOption: () => {
                    const currentVal = shadeOptions.HeaderMountType.getValue();
                    const match = (mountTypeOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            HembarLine: {
                ...shadeOptions.HembarLine,
                options: hembarLineOpts,
                getSelectedOption: () => {
                    const currentVal = shadeOptions.HembarLine.getValue();
                    const match = (hembarLineOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            HembarStyle: {
                ...shadeOptions.HembarStyle,
                options: hembarStyleOpts,
                getSelectedOption: () => {
                    const currentVal = shadeOptions.HembarStyle.getValue();
                    const match = (hembarStyleOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            ControlSide: {
                ...shadeOptions.ControlSide,
                options: controlSideOpts,
                getSelectedOption: () => {
                    const currentVal = shadeOptions.ControlSide.getValue();
                    const match = (controlSideOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            SideChannels: {
                ...shadeOptions.SideChannels,
                options: sideChannelOpts,
                getSelectedOption: () => {
                    const currentVal = shadeOptions.SideChannels.getValue();
                    const match = (sideChannelOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
            EndCaps: {
                ...shadeOptions.EndCaps,
                options: endCapsOpts,
                getSelectedOption: () => {
                    const currentVal = shadeOptions.EndCaps.getValue();
                    const match = (endCapsOpts as any[]).find((opt) => opt?.value === currentVal);
                    return match ?? { label: "", value: "", title: "" };
                },
            },
        } as typeof shadeOptions;
    }, [shadeOptions, sharedDriveTypeOptions, sharedShadeConfigOptions, sharedMountTypeOptions, sharedHembarStyleOptions, sharedHembarLineOptions, sharedControlSideOptions, sharedSideChannelOptions, sharedEndCapsOptions, unanimousShadeConfig]);

    const coupledOptions: IndoorShadeOptionsCoupledDict = IndoorShadeOptionsCoupled(
        workingAssembly,
        setWorkingAssembly,
        { fabrics: indoorFabrics, user, quote, assemblyOptions, quoteCombinations }
    );

    const [coupledOptionsLoaded, setCoupledOptionsLoaded] = useState(false);

    useEffect(() => {
        if (!coupledOptionsLoaded) {
            setCoupledOptionsLoaded(true);
        }
    }, [coupledOptions, coupledOptionsLoaded]);

    const selectedCount = selectedAssemblies.length;
    const allSelectedCoupled = selectedAssemblies.length > 0
        && selectedAssemblies.every((assy) => !!assy.is_coupled);

    // Pricing/error state and derived errors
    const [msrp, setMsrp] = useState(initAssyState.msrp);
    const [errorsFromBackend, setErrors] = useState(initAssyState.error_messages ?? "");
    const quoteStore = useQuoteIdAndDispatch(quoteId);
    const errorWarningSet = useAssemblyErrorsByStore(
        initAssyState,
        quoteStore ?? initialQuote(),
        assemblies,
        quoteCombinations ?? { Fascias: [] },
        finalShadeOptions
    );
    const feErrors = errorWarningSet?.errors ?? [];
    let errorList = [
        ...feErrors.filter((e) => !errorsFromBackend.includes(e.message.trim())).map((error) => error.message),
    ];
    if (errorsFromBackend !== "") {
        errorList = [errorsFromBackend];
    }
    const errors = errorList.filter((error) => error !== "").reduce((acc, error) => `${acc + error} `, "");

    const sortShadesByRowAndColumn = (shades: ShadeStore[]): ShadeStore[] =>
        // Keep shade rows ordered so band math behaves consistently
        [...shades].sort((a, b) => {
            if (a.row_coordinate === b.row_coordinate) {
                return a.column_coordinate - b.column_coordinate;
            }
            return a.row_coordinate - b.row_coordinate;
        });

    const getCoupledWidths = (assembly: AssemblyStore): number[] => {
        const shades = assembly.shades ?? [];
        if (shades.length === 0) return [];
        const maxColumn = shades.reduce((max, shade) => (
            shade.column_coordinate > max ? shade.column_coordinate : max
        ), -1);
        if (maxColumn < 0) return [];
        const widths: number[] = [];
        for (let column = 0; column <= maxColumn; column += 1) {
            // Copy any existing width for this column (default to 0)
            const columnShade = shades.find((shade) => shade.column_coordinate === column);
            widths.push(columnShade?.width ?? 0);
        }
        return widths;
    };

    const coupledWidthsDiffer = (baseline: number[], current: number[]): boolean => {
        const maxLength = Math.max(baseline.length, current.length);
        for (let i = 0; i < maxLength; i += 1) {
            const baseVal = baseline[i] ?? 0;
            const currentVal = current[i] ?? 0;
            if (baseVal !== currentVal) {
                return true;
            }
        }
        return false;
    };

    const syncAssemblyBandCount = (assembly: AssemblyStore, targetBandCount: number): AssemblyStore => {
        if (targetBandCount < 0) return assembly;
        const currentShades = (assembly.shades ?? []).map((shade) => ({ ...shade }));
        const normalizedTarget = Math.max(targetBandCount, 0);
        const templateFront = currentShades.find((shade) => shade.row_coordinate === 0 && shade.column_coordinate === 0)
            ?? { ...InitialShadeState(), row_coordinate: 0 };
        const templateBack = currentShades.find((shade) => shade.row_coordinate === 1 && shade.column_coordinate === 0);
        let updatedShades = currentShades;

        const maxExistingColumn = updatedShades.reduce((max, shade) => (
            shade.column_coordinate > max ? shade.column_coordinate : max
        ), -1);
        // Keep track of band count in order to determine whether to add or remove rows
        const existingBandCount = maxExistingColumn >= 0 ? (maxExistingColumn + 1) : 0;

        if (normalizedTarget > existingBandCount) {
            for (let column = 0; column < normalizedTarget; column += 1) {
                if (!updatedShades.some((shade) => shade.row_coordinate === 0 && shade.column_coordinate === column)) {
                    // Seed a blank front-row shade for the new column
                    updatedShades.push({
                        ...templateFront,
                        id: 0,
                        column_coordinate: column,
                        row_coordinate: 0,
                        width: 0,
                    });
                }
                if (templateBack) {
                    if (!updatedShades.some((shade) => shade.row_coordinate === 1 && shade.column_coordinate === column)) {
                        // Mirror a blank back-row shade when dual rows exist
                        updatedShades.push({
                            ...templateBack,
                            id: 0,
                            column_coordinate: column,
                            row_coordinate: 1,
                            width: 0,
                        });
                    }
                }
            }
        }

        if (normalizedTarget < existingBandCount) {
            updatedShades = updatedShades.filter((shade) => shade.column_coordinate < normalizedTarget);
        }

        updatedShades = updatedShades.map((shade) => ({
            // Clear widths for any trimmed columns beyond the target
            ...shade,
            width: shade.column_coordinate < normalizedTarget ? (shade.width ?? 0) : 0,
        }));

        return {
            ...assembly,
            band_count: normalizedTarget,
            shades: sortShadesByRowAndColumn(updatedShades),
        };
    };

    const applyCoupledWidthsToAssembly = (assembly: AssemblyStore, widths: number[]): AssemblyStore => {
        if (!Array.isArray(assembly.shades) || assembly.shades.length === 0) {
            return assembly;
        }

        const updatedShades = assembly.shades.map((shade) => ({
            // Apply the shared coupled width for this column
            ...shade,
            width: widths[shade.column_coordinate] ?? 0,
        }));

        return {
            ...assembly,
            shades: updatedShades,
        };
    };

    // Ensure assembly reflects a true "Single" configuration
    const normalizeAssemblyToSingle = (assembly: AssemblyStore): AssemblyStore => {
        // Keep only the front-row, first-column shade; seed a default if missing
        const frontFirst = (assembly.shades ?? []).find((s) => s.row_coordinate === 0 && s.column_coordinate === 0)
            ?? { ...InitialShadeState(), row_coordinate: 0, column_coordinate: 0, id: 0 };
        return {
            ...assembly,
            is_dual: false,
            is_coupled: false,
            band_count: 1,
            shades: [
                {
                    ...frontFirst,
                    row_coordinate: 0,
                    column_coordinate: 0,
                    id: frontFirst.id ?? 0,
                }
            ],
        };
    };

    // Determine changes and apply to selected assemblies
    const setAssembly = () => {
        try {
            // Gather changes to editable top-level assembly properties
            const excludedTopLevelKeys = new Set([
                "id",
                "quote_id",
                "sequence_id",
                "msrp",
                "shipping",
                "cost",
                "labor",
                "markup",
                "overhead",
                "loadStatus",
                "timeLoaded",
                "last_time_priced",
                "last_time_priced_value_saved",
                "front_row_torque",
                "front_row_weight",
                "back_row_torque",
                "back_row_weight",
                "error_messages",
                "mfg_status",
                "series_id",
                "shades",
                "colors",
            ] as const);

            const topLevelChanges: Partial<AssemblyStore> = {};
            (Object.keys(workingAssembly) as Array<keyof AssemblyStore | string>).forEach((k) => {
                const key = k as keyof AssemblyStore;
                if ((excludedTopLevelKeys as unknown as Set<string>).has(key as unknown as string)) return;
                const wVal = (workingAssembly as any)[key];
                const iVal = (initAssyState as any)[key];
                if (wVal !== iVal) {
                    (topLevelChanges as any)[key] = wVal;
                }
            });

            // Gather changed colors
            const changedColors: Record<string, string> = {};
            const workingColors = (workingAssembly.colors ?? {}) as Record<string, string | undefined>;
            const initialColors = (initAssyState.colors ?? {}) as Record<string, string | undefined>;
            const colorKeys = Array.from(new Set([...Object.keys(workingColors), ...Object.keys(initialColors)]));
            colorKeys.forEach((cKey) => {
                const w = workingColors[cKey];
                const i = initialColors[cKey];
                if (w !== i && w !== undefined) {
                    changedColors[cKey] = String(w);
                }
            });

            const paletteChanged = workingAssembly.palette_color !== initAssyState.palette_color;
            if (paletteChanged) {
                topLevelChanges.palette_color = workingAssembly.palette_color;
            }

            // Gather shade band changes, by row
            // TODO: Apply this logic to initial shared shade band gathering
            type ShadeDiff = { [prop: string]: any };
            const shadeDiffsByRow: Record<number, ShadeDiff> = {};
            const maxRowsToCheck = Math.max(workingAssembly.shades?.length ?? 0, initAssyState.shades?.length ?? 0);
            for (let rowIdx = 0; rowIdx < maxRowsToCheck; rowIdx += 1) {
                const wShade = workingAssembly.shades?.[rowIdx];
                const iShade = initAssyState.shades?.[rowIdx];
                if (wShade && iShade) {
                    const excludedShadeKeys = new Set([
                    "id",
                    "row_coordinate",
                    "column_coordinate",
                    "make_this_shade",
                    "roll_diameter",
                    "clearence_diameter",
                    "torque_needed",
                    "deflection",
                    "weight",
                    "api_motor_id",
                    "tube_sku",
                    "bearing_pin_sku",
                    "tube_name",
                    "mfg_status",
                    "width",
                ] as const);

                    const diff: ShadeDiff = {};
                    Object.keys(wShade).forEach((sKey) => {
                        if ((excludedShadeKeys as unknown as Set<string>).has(sKey)) return;
                        const wVal = (wShade as any)[sKey];
                        const iVal = (iShade as any)[sKey];
                        if (wVal !== iVal) {
                            // Track per-row edits to apply them to each assembly
                            diff[sKey] = wVal;
                        }
                    });

                    if (Object.keys(diff).length > 0) {
                        shadeDiffsByRow[rowIdx] = diff;
                    }
                }
            }

            // Compare coupled widths separately in order to address them per column
            const initialCoupledWidths = getCoupledWidths(initAssyState);
            const workingCoupledWidths = getCoupledWidths(workingAssembly);
            const coupledWidthChanged = coupledWidthsDiffer(initialCoupledWidths, workingCoupledWidths);

            // If no changes detected, abort unless ShadeConfiguration was explicitly changed in this session
            const hasTopLevel = Object.keys(topLevelChanges).length > 0;
            const hasColors = Object.keys(changedColors).length > 0;
            const hasShadeDiffs = Object.keys(shadeDiffsByRow).length > 0;
            const hasAnyShadeChanges = hasShadeDiffs || coupledWidthChanged;
            const hasExplicitShadeConfigChange = !!editedShadeConfig;
            const bandCountEdited = Object.prototype.hasOwnProperty.call(topLevelChanges, "band_count");
            const desiredBandCount = typeof workingAssembly.band_count === "number"
                ? workingAssembly.band_count
                : (initAssyState.band_count ?? 0);
            if (!hasTopLevel && !hasColors && !hasAnyShadeChanges && !hasExplicitShadeConfigChange) return;

            // If name changed, append numbers to shade names to ensure uniqueness
            const baseShadeName = (topLevelChanges as Partial<AssemblyStore>).shade_name;
            const otherNames = assemblies
                .filter((a) => !selectedAssemblies.some((sa) => sa.sequence_id === a.sequence_id))
                .map((a) => (a.shade_name ?? '').trim())
                .filter((n) => n !== '');
            const usedNames = new Set<string>(otherNames);

            const makeUniqueName = (desired: string): string => {
                const name = desired.trim();
                if (name === '') return name;
                if (!usedNames.has(name)) {
                    usedNames.add(name);
                    return name;
                }
                let i = 2;
                let candidate = `${name} (${i})`;
                while (usedNames.has(candidate)) {
                    i += 1;
                    candidate = `${name} (${i})`;
                }
                usedNames.add(candidate);
                return candidate;
            };

            // Construct updated assemblies
            const paletteName = workingAssembly.palette_color;
            const assembliesToUpdate: AssemblyStore[] = selectedAssemblies.map((assy) => {
                let updated: AssemblyStore = { ...assy } as AssemblyStore;

                if (hasTopLevel) {
                    const changes: Partial<AssemblyStore> = { ...topLevelChanges };
                    if (typeof baseShadeName === 'string') {
                        changes.shade_name = makeUniqueName(baseShadeName);
                    }
                    updated = { ...updated, ...changes } as AssemblyStore;
                }

                if (topLevelChanges.palette_color !== undefined) {
                    // Palette changed: Mimic singl shade flow to rebuild colors from palette defaults
                    if (typeof paletteName === "string" && paletteName !== "" && paletteName !== "Custom Palette") {
                        const paletteDefaults = computePaletteColors(updated, paletteName);
                        updated = {
                            ...updated,
                            colors: paletteDefaults,
                            hardware_color: typeof workingAssembly.hardware_color === "string" ? workingAssembly.hardware_color : updated.hardware_color,
                        };
                    } else {
                        updated = {
                            ...updated,
                            palette_color: paletteName ?? "",
                            hardware_color: typeof workingAssembly.hardware_color === "string" ? workingAssembly.hardware_color : updated.hardware_color,
                            colors: { ...(updated.colors ?? {}) },
                        };
                    }
                }

                if (hasColors) {
                    // Manual overrides from the modal always win—apply them after palette defaults.
                    updated = {
                        ...updated,
                        colors: {
                            ...(updated.colors ?? {}),
                            ...changedColors,
                        }
                    };
                }

                if (hasShadeDiffs && Array.isArray(updated.shades)) {
                    const byRow0 = shadeDiffsByRow[0];
                    const byRow1 = shadeDiffsByRow[1];

                    // Ensure we don't apply conflicting motor/manual fields across different drive types
                    const sanitizeDiffForShade = (shade: ShadeStore, diff: Record<string, any>) => {
                        const out = { ...diff };
                        if (shade.shade_type === "motorized") {
                            // Motorized: keep motor_type, drop manual_chain
                            if ("manual_chain" in out) delete out.manual_chain;
                        } else if (shade.shade_type === "manual") {
                            // Manual: keep manual_chain, drop motor_type
                            if ("motor_type" in out) delete out.motor_type;
                        }
                        return out;
                    };

                    updated = {
                        ...updated,
                        shades: updated.shades.map((s) => {
                            const baseDiff = (
                                s.row_coordinate === 0
                                    ? byRow0
                                    : (s.row_coordinate === 1
                                        ? (byRow1 ?? byRow0)
                                        : undefined)
                            );

                            if (!baseDiff) return s;

                            const sanitized = sanitizeDiffForShade(s, baseDiff);
                            return Object.keys(sanitized).length > 0 ? { ...s, ...sanitized } : s;
                        })
                    };
                }

                // If the target configuration was explicitly changed in this session, normalize structure accordingly
                const targetConfig = editedShadeConfig as IndoorShadeConfiguration | null;
                if (targetConfig) {
                    const frontFirst = (updated.shades ?? []).find((s) => s.row_coordinate === 0 && s.column_coordinate === 0)
                        ?? { ...InitialShadeState(), row_coordinate: 0, column_coordinate: 0, id: 0 };
                    const backBase = (updated.shades ?? []).find((s) => s.row_coordinate === 1 && s.column_coordinate === 0)
                        ?? { ...frontFirst, row_coordinate: 1 };

                    const buildSingle = (): AssemblyStore => normalizeAssemblyToSingle(updated);

                    const buildDual = (): AssemblyStore => ({
                        ...updated,
                        is_dual: true,
                        is_coupled: false,
                        band_count: 1,
                        shades: sortShadesByRowAndColumn([
                            { ...frontFirst, row_coordinate: 0, column_coordinate: 0 },
                            { ...backBase, row_coordinate: 1, column_coordinate: 0 },
                        ]),
                    });

                    const buildCoupled = (): AssemblyStore => {
                        const targetBands = Math.max(2, desiredBandCount || 0);
                        const cols: ShadeStore[] = [] as any;
                        for (let c = 0; c < targetBands; c += 1) {
                            cols.push({ ...frontFirst, row_coordinate: 0, column_coordinate: c });
                        }
                        return {
                            ...updated,
                            is_dual: false,
                            is_coupled: true,
                            band_count: targetBands,
                            shades: sortShadesByRowAndColumn(cols),
                        };
                    };

                    const buildDualCoupled = (): AssemblyStore => {
                        const targetBands = Math.max(2, desiredBandCount || 0);
                        const cells: ShadeStore[] = [] as any;
                        for (let c = 0; c < targetBands; c += 1) {
                            cells.push({ ...frontFirst, row_coordinate: 0, column_coordinate: c });
                            cells.push({ ...backBase, row_coordinate: 1, column_coordinate: c });
                        }
                        return {
                            ...updated,
                            is_dual: true,
                            is_coupled: true,
                            band_count: targetBands,
                            shades: sortShadesByRowAndColumn(cells),
                        };
                    };

                    if (targetConfig === "single") updated = buildSingle();
                    else if (targetConfig === "dual") updated = buildDual();
                    else if (targetConfig === "coupled") updated = buildCoupled();
                    else if (targetConfig === "dual_coupled") updated = buildDualCoupled();

                    // For coupled configurations, force band sync and width propagation using current working assembly values
                    if (targetConfig === "coupled" || targetConfig === "dual_coupled") {
                        const targetBands = Math.max(2, desiredBandCount || 0);
                        updated = syncAssemblyBandCount(updated, targetBands);
                        // Always apply working widths to ensure CoupledWidth edits propagate
                        updated = applyCoupledWidthsToAssembly(updated, workingCoupledWidths);
                    }
                }

                // Apply generic band/width sync only when we didn't explicitly handle it via ShadeConfiguration change
                if (!targetConfig) {
                    const needsBandSync = bandCountEdited
                        || (coupledWidthChanged && desiredBandCount > (updated.band_count ?? 0));
                    if (needsBandSync) {
                        // Rebuild the shade grid to match the latest band count before applying widths
                        updated = syncAssemblyBandCount(updated, desiredBandCount);
                    }

                    if (coupledWidthChanged) {
                        // Apply the shared coupled widths to every synchronized column
                        updated = applyCoupledWidthsToAssembly(updated, workingCoupledWidths);
                    }
                }

                updated.quote_id = assy.quote_id;
                updated.sequence_id = assy.sequence_id;

                return updated;
            });

            // Apply updates if any
            if (assembliesToUpdate.length > 0) {
                store.dispatch(updateAssemblies({ quoteId, assemblies: assembliesToUpdate }));
            }
        } catch (err) {
            console.error("### Mass edit: failed to set assemblies", err);
        }
    };

    // Reset and close modal
    const closeAll = useCallback(() => {
        setIsColorPickerOpen(false);
        setAssembliesLoaded(false);
        setEditedShadeConfig(null);
        onClose();
    }, [onClose]);

    // Initialize working assembly with shared values from selected assemblies
    const initializeWorkingAssy = useCallback(() => {
            setAssembliesLoaded(true);

            // Base assembly state to be populated with shared values
            const sharedAssembly = {
                ...InitialAssemblyState(),
                unlocked: true,
                shades: [InitialShadeState()]
            };

            // Gather shared top-level assembly properties
            Object.keys(sharedAssembly).forEach((key) => {
                if (key !== 'shades' && selectedAssemblies[0]) {
                    const firstValue = (selectedAssemblies[0] as any)[key];
                    const isShared = selectedAssemblies.every((assy) => (assy as any)[key] === firstValue);
                    if (isShared) {
                        (sharedAssembly as any)[key] = firstValue;
                    }
                }
            });

            // Gather shared shade band properties
            // TODO: only first 2 shades supported, should support more if needed
            if (
                selectedAssemblies[0]
                && selectedAssemblies[0].shades
                && selectedAssemblies[0].shades[0]
                && sharedAssembly.shades[0]
            ) {
                Object.keys(sharedAssembly.shades[0]).forEach((shadeKey) => {
                    const firstValue = selectedAssemblies[0]?.shades?.[0]?.[shadeKey];
                    const isShared = selectedAssemblies.every((assy) =>
                        assy.shades && assy.shades[0] && assy.shades[0][shadeKey] === firstValue);
                    if (isShared && sharedAssembly.shades[0]) {
                        (sharedAssembly.shades[0] as any)[shadeKey] = firstValue;
                    }
                });

                if (selectedAssemblies[0]?.shades?.[1]) {
                    sharedAssembly.shades.push(InitialShadeState());
                    if (sharedAssembly.shades[1]) {
                        Object.keys(sharedAssembly.shades[1]).forEach((shadeKey) => {
                            const firstValue = selectedAssemblies[0]?.shades?.[1]?.[shadeKey];
                            const isShared = selectedAssemblies.every((assy) =>
                                assy.shades && assy.shades[1] && assy.shades[1][shadeKey] === firstValue);
                            if (isShared && sharedAssembly.shades[1]) {
                                (sharedAssembly.shades[1] as any)[shadeKey] = firstValue;
                            }
                        });
                    }
                }
            }

            // Gather shared colors
            try {
                const sharedColors: Record<string, string> = {};
                const colorKeySet = new Set<string>();
                selectedAssemblies.forEach((assy) => {
                    const c = (assy.colors ?? {}) as Record<string, string | undefined>;
                    Object.keys(c).forEach((k) => colorKeySet.add(k));
                });
                const keys = Array.from(colorKeySet);
                keys.forEach((k) => {
                    const first = (selectedAssemblies[0]?.colors ?? {})[k];
                    const isShared = selectedAssemblies.every((assy) => (assy.colors ?? {})[k] === first);
                    if (typeof first === 'string' && first !== '' && isShared) {
                        sharedColors[k] = first;
                    }
                });
                (sharedAssembly as any).colors = sharedColors;
            } catch (e) {
                console.error("### Mass edit: failed to determine shared colors", e);
            }

            // If all MANUAL shades share the same manual type, prefill it for display
            try {
                const manualAssemblies = selectedAssemblies.filter((a) => a?.shades?.[0]?.shade_type === "manual");
                if (manualAssemblies.length > 0) {
                    const firstManual = manualAssemblies[0]?.shades?.[0]?.manual_chain ?? "";
                    const allSameManual = firstManual !== "" && manualAssemblies.every((a) => (a?.shades?.[0]?.manual_chain ?? "") === firstManual);
                    if (allSameManual && sharedAssembly.shades?.[0]) {
                        sharedAssembly.shades[0].manual_chain = firstManual as any;
                    }
                }
            } catch (e) {
                console.error("### Mass edit: failed to determine shared manual type", e);
            }

            // If all MOTORIZED shades share the same motor type, prefill it for display
            try {
                const motorAssemblies = selectedAssemblies.filter((a) => a?.shades?.[0]?.shade_type === "motorized");
                if (motorAssemblies.length > 0) {
                    const firstMotor = motorAssemblies[0]?.shades?.[0]?.motor_type ?? "";
                    const allSameMotor = firstMotor !== "" && motorAssemblies.every((a) => (a?.shades?.[0]?.motor_type ?? "") === firstMotor);
                    if (allSameMotor && sharedAssembly.shades?.[0]) {
                        sharedAssembly.shades[0].motor_type = firstMotor as any;
                    }
                }
            } catch (e) {
                console.error("### Mass edit: failed to determine shared motor type", e);
            }

            const firstBandCount = selectedAssemblies[0]?.band_count ?? 0;
            const bandCountsMatch = selectedAssemblies.length > 0
                && firstBandCount > 0
                && selectedAssemblies.every((assy) => (assy.band_count ?? 0) === firstBandCount);

            if (bandCountsMatch) {
                // Only initialize and show coupled shades table when every selected assembly shares the same band count
                setShowBands(true);
                sharedAssembly.band_count = firstBandCount;

                const sharedFrontTemplate: ShadeStore = {
                    ...((sharedAssembly.shades ?? []).find((shade) => shade.row_coordinate === 0) ?? {
                        ...InitialShadeState(),
                        row_coordinate: 0,
                    }),
                    id: 0,
                };

                const hasBackRow = selectedAssemblies.some((assy) => assy.shades?.some((shade) => shade.row_coordinate === 1));
                const sharedBackTemplate: ShadeStore | undefined = hasBackRow
                    ? {
                        ...((sharedAssembly.shades ?? []).find((shade) => shade.row_coordinate === 1) ?? {
                            ...InitialShadeState(),
                            row_coordinate: 1,
                        }),
                        id: 0,
                    }
                    : undefined;

                const getSharedWidthForColumn = (column: number): number | undefined => {
                    // If every assembly shares a width for this column, reuse it
                    const firstWidth = selectedAssemblies[0]?.shades?.find((shade) =>
                        shade.row_coordinate === 0 && shade.column_coordinate === column)?.width ?? 0;
                    const allMatch = selectedAssemblies.every((assy) => (
                        (assy.shades ?? []).some((shade) => (
                            shade.row_coordinate === 0
                            && shade.column_coordinate === column
                            && (shade.width ?? 0) === (firstWidth ?? 0)
                        ))
                    ));
                    return allMatch ? firstWidth ?? 0 : undefined;
                };

                const coupledShades: ShadeStore[] = [];
                for (let column = 0; column < firstBandCount; column += 1) {
                    const sharedWidth = getSharedWidthForColumn(column);

                    coupledShades.push({
                        // Clone the front-row template for each column
                        ...sharedFrontTemplate,
                        column_coordinate: column,
                        row_coordinate: sharedFrontTemplate.row_coordinate ?? 0,
                        width: sharedWidth ?? 0,
                    });

                    if (sharedBackTemplate) {
                        coupledShades.push({
                            // Clone the back-row template when dual/dual-coupled shades exist
                            ...sharedBackTemplate,
                            column_coordinate: column,
                            row_coordinate: sharedBackTemplate.row_coordinate ?? 1,
                            width: sharedWidth ?? 0,
                        });
                    }
                }

                sharedAssembly.shades = sortShadesByRowAndColumn(coupledShades);
            } else {
                setShowBands(false);
                sharedAssembly.band_count = 0;
                // Keep the shared front-row shade fields
                const preservedFront = {
                    ...(sharedAssembly.shades?.find((s) => s.row_coordinate === 0) ?? InitialShadeState()),
                    row_coordinate: 0,
                    column_coordinate: 0,
                    id: 0,
                };
                sharedAssembly.shades = [preservedFront];
            }

            // Assign gathered shared assembly state
            setInitAssyState(sharedAssembly);
            setWorkingAssembly(sharedAssembly);
    }, [selectedAssemblies]);

    // Run initialization on open if needed
    useEffect(() => {
        if (isOpen && selectedCount > 0 && !assembliesLoaded) {
            initializeWorkingAssy();
        }
    }, [assembliesLoaded, initializeWorkingAssy, isOpen, selectedCount]);

    // Prepare to re-initialize on next open if selection changes
    useEffect(() => {
        if (!isOpen) {
            setWorkingAssembly(InitialAssemblyState());
            setAssembliesLoaded(false);
            setEditedShadeConfig(null);
        }
    }, [isOpen]);

    // Reset working assembly on open/close
    useEffect(() => {
        setWorkingAssembly(initAssyState);
    }, [isOpen, initAssyState]);

    // Close modal on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                closeAll();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [popupRef, closeAll]);

    // Price calculation effect
    useEffect(() => {
        if (quoteId === 0 || !isOpen) return;
        setMsrp(0);
        setErrors("");
        if (feErrors.length > 0) return;
        apiCalls
            .calculateAssemblyPriceNew(AssemblyViewModelFromAssemblyStore(initAssyState), quoteId, "Indoor")
            .then((res) => {
                setMsrp(res?.data?.msrp ?? 0);
                const errorMessage = res?.data?.errors.map((e) => e.message);
                const errorMessageListAsOneString = errorMessage?.join(", ") ?? "";
                setErrors(errorMessageListAsOneString);
            });
    }, [initAssyState, isOpen, quoteId, feErrors.length]);

    // On done, apply changes (if any) and close
    const submitShade = () => {
        const assemblyChanged = JSON.stringify(workingAssembly) !== JSON.stringify(initAssyState);
        if (assemblyChanged || editedShadeConfig) setAssembly();
        return closeAll();
    };

    if (!isOpen) return null;

    return (
        <Popup open={isOpen} onClose={closeAll} className="mobile-modal p-2 m-2">
            <Row>
                <h2>Mass Edit Shade</h2>
            </Row>
            <MassEditIndoorInner
                        options={finalShadeOptions}
                        quoteType={quoteType}
                        fabrics={indoorFabrics}
                        isNotAdd
                        massEdit
                        unlocked={initAssyState.unlocked}
                        errorWarningSet={errorWarningSet}
                        pickerState={isColorPickerOpen}
                        setPickerState={setIsColorPickerOpen}
                        showBands={showBands}
                        onBandOverrideSelect={() => setShowBands(true)}
                        coupledOptions={coupledOptions}
                        allowBandOverride={allSelectedCoupled}
                        onShadeConfigurationEdit={(val) => {
                            setEditedShadeConfig(val || null);
                            if (val === "coupled" || val === "dual_coupled") {
                                setShowBands(true);
                            } else if (val === "single" || val === "dual") {
                                setShowBands(false);
                            }
                        }}
            />
            {!isColorPickerOpen && (
            <PriceErrors quoteId={quoteId} msrp={msrp} error_messages={msrp !== 0 ? "" : errors ?? ""} assembly={workingAssembly} noLoading />
            )}
            <Col className="d-flex flex-column align-items-center justify-content-center">
                <Button
                    className="col-lg-4"
                    onClick={(
                        isColorPickerOpen
                            ? () => setIsColorPickerOpen(false)
                            : submitShade
                        )}
                >
                    Done
                </Button>
                {
                    !initAssyState.unlocked
                    ? <Button
                        className="col-lg-4 mt-1"
                        onClick={() => {
                            setWorkingAssembly({ ...workingAssembly, unlocked: true });
                                }}
                    >
                        <FontAwesomeIcon icon={faLock} />
                    </Button>
                    : null
                }
            </Col>
        </Popup>
        );
};

export default MassEditIndoor;
